import { CheckboxCellRenderer, Select, type TypeSelectOption, type TypeTableColumn } from '@thefarmersfront/kitchen';

import { NumberInputCell } from '~/components/common';

import { formatNumber, getSelectPlacement } from '~/utils/common';

import type { TypeReturnRegisterSecondStepValues, TypeReturnRegisterSecondStepValuesState } from '~/types/claim.types';

type TypeReturnOrderItem = TypeReturnRegisterSecondStepValues['returnOrderItems'][number];
type TypeReturnOrderItemState = TypeReturnRegisterSecondStepValuesState['returnOrderItems'][number];

/** 표 행. 입력 검증 상태를 행에 함께 실어, 상태가 바뀌어도 컬럼을 새로 만들지 않고 셀만 다시 그린다. */
export type TypeReturnGoodsRow = TypeReturnOrderItem & { state: TypeReturnOrderItemState | undefined };

/** 행 입력 핸들러. 컬럼을 고정하려고 최신 값은 핸들러가 직접 읽는다(렌더마다 바뀌는 값을 컬럼에 넣지 않는다). */
export type TypeReturnGoodsHandlers = {
  onToggleReturn: (itemId: string, checked: boolean) => void;
  onChangeQuantity: (itemId: string, value: string) => void;
  onBlurQuantity: (itemId: string, value: string) => void;
  onFocusQuantity: (itemId: string) => void;
  onChangeReason: (itemId: string, value: TypeSelectOption<string>) => void;
};

/** 반품사유 드롭다운을 위로 펼치기 시작하는 행 위치(아래쪽 행은 표 밖으로 잘리지 않게 위로 연다). */
const SELECT_PLACEMENT_THRESHOLD_ROW = 5;

/**
 * [반품 신청/조회] 반품 신청 등록 모달 > 반품 정보 입력 > '반품 상품' 테이블 컬럼 정의
 */
const getClaimReturnRegisterReturnItemsColumnDefs = ({
  selectOptions,
  handlers,
}: {
  selectOptions: TypeSelectOption<string>[];
  handlers: TypeReturnGoodsHandlers;
}) => {
  const columnDefs: TypeTableColumn<TypeReturnGoodsRow>[] = [
    {
      accessorKey: 'itemId',
      header: '상품코드',
      size: 130,
    },
    {
      accessorKey: 'itemName',
      header: '상품명',
      minSize: 365,
      meta: { flex: true },
    },
    {
      accessorKey: 'isReturn',
      header: '반품여부',
      size: 76,
      minSize: 76,
      cell: ({ row }) => (
        <CheckboxCellRenderer
          label="반품"
          checked={row.original.isReturn}
          onChange={(e) => handlers.onToggleReturn(row.original.itemId, e.target.checked)}
        />
      ),
    },
    {
      accessorKey: 'quantity',
      header: '구매수량',
      size: 79,
      cell: ({ getValue }) => {
        const value = getValue<number>();
        return value ? formatNumber(value) : '-';
      },
      meta: { cellStyle: { textAlign: 'right' } },
    },
    {
      accessorKey: 'returnQuantity',
      header: '반품수량',
      meta: { headerTooltip: { description: '필수 입력입니다.', maxWidth: '103px' } },
      size: 100,
      minSize: 100,
      cell: ({ getValue, row }) => {
        const data = row.original;
        return (
          <NumberInputCell
            value={getValue<string>()}
            disabled={!data.isReturn}
            status={data.state?.returnQuantity.status}
            validationMessage={data.state?.returnQuantity.message}
            onChange={(value) => handlers.onChangeQuantity(data.itemId, value)}
            onBlur={(value) => handlers.onBlurQuantity(data.itemId, value)}
            onFocus={() => handlers.onFocusQuantity(data.itemId)}
          />
        );
      },
    },
    {
      id: 'returnReason',
      header: '반품사유',
      meta: { headerTooltip: { description: '필수 선택입니다.', maxWidth: '103px' } },
      size: 147,
      minSize: 147,
      cell: ({ row, table }) => {
        const data = row.original;
        return (
          <Select
            size="tiny"
            placeholder="선택"
            dropdownPortalTarget={document.body}
            shouldCloseOnNotInView
            dropdownPlacement={getSelectPlacement(row.index, table.getRowModel().rows.length, {
              thresholdRow: SELECT_PLACEMENT_THRESHOLD_ROW,
            })}
            disabled={!data.isReturn}
            options={selectOptions}
            status={data.state?.returnReason.status}
            validationMessage={data.state?.returnReason.message}
            value={data.returnReason ?? null}
            onChange={(_: unknown, value: TypeSelectOption<string>) => handlers.onChangeReason(data.itemId, value)}
          />
        );
      },
    },
  ];

  return { columnDefs };
};

export default getClaimReturnRegisterReturnItemsColumnDefs;
